import { useState, useRef, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Github, Twitter, Linkedin, Upload, Edit2, Save, Briefcase, Star, Folder, Users, MapPin, Bell, ArrowLeft } from "lucide-react";
import { cn } from "../lib/utils"
import GitHubCalendar from 'react-github-calendar';
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import '../index.css';

const socialIcons = {
  github: Github,
  twitter: Twitter,
  linkedin: Linkedin,
};

type Profile = {
  name: string;
  username: string;
  about: string;
  location: string;
  email: string;
  github: string;
  wallet: string;
  twitter: string;
  linkedin: string;
  image: string;
  skills: string[];
  readme?: string;
  workExperience: {
    company: string;
    role: string;
    duration: string;
    period: string;
    logo?: string;
  }[];
};

// Dummy data for new sections
const devfolioStats = [
  { label: "Projects", value: 23 },
  { label: "Prizes", value: 15 },
  { label: "Hackathons", value: 45 },
  { label: "Hackathons Org.", value: 0 },
];
const githubStats = {
  contributions: 333,
  stars: 30,
  repos: 219,
  followers: 139,
  projects: [
    { name: "PayStream", lang: "JavaScript", stars: 9, forks: 0, issues: 0 },
    { name: "salarydexyz", lang: "JavaScript", stars: 3, forks: 0, issues: 0 },
    { name: "coindcxnext", lang: "JavaScript", stars: 9, forks: 4, issues: 4 },
    { name: "MicroPay-Hackathon", lang: "JavaScript", stars: 8, forks: 1, issues: 1 },
  ],
};
const workExperience = [
  {
    company: "EPNS",
    role: "Backend Developer",
    duration: "2 years 7 months",
    period: "Oct '22 - Present",
    logo: "https://devfolio.co/logos/epns.png",
  },
];
const topProjects = [
  {
    name: "Unified eKYC Consortium",
    desc: "The KYC system powered by the security of Blockchain and...",
    icon: "U",
    color: "bg-orange-200 text-orange-800",
  },
  {
    name: "CrowdChain - Blockchain Crowdfunding",
    desc: "Being Kind is Being Transparent",
    icon: "C",
    color: "bg-yellow-200 text-yellow-800",
  },
  {
    name: "MicroPay",
    desc: "Fast, Secure, Decentralized",
    icon: "M",
    color: "bg-teal-200 text-teal-800",
  },
  {
    name: "Salaried3.xyz",
    desc: "Undercollateralized Borrow against Future Salaries",
    icon: "S",
    color: "bg-gray-200 text-gray-800",
  },
];

export default function Profile() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile>({
    name: "RIZ DOME",
    username: "riz",
    about: "Here you can add bio",
    location: "Chennai, India",
    email: "riz@example.com",
    github: "riz",
    wallet: "0x1234567890abcdef",
    twitter: "riz",
    linkedin: "riz",
    image: "https://randomuser.me/api/portraits/men/32.jpg",
    skills: ["Solidity", "C++", "TypeScript", "Node.js", "Next.js"],
    readme: "# About Me\n\nI'm a web3 developer passionate about building decentralized applications...",
    workExperience: [
      {
        company: "EPNS",
        role: "Backend Developer",
        duration: "2 years 7 months",
        period: "Oct '22 - Present",
        logo: "https://devfolio.co/logos/epns.png",
      }
    ]
  });
  const [editing, setEditing] = useState(false);
  const [newSkill, setNewSkill] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper for input style
  const inputClass =
    "border-2 border-black rounded-lg px-4 py-2 bg-[#FAF94F] shadow-[2px_2px_0px_#000] text-black font-semibold placeholder:text-[#888] focus:outline-none focus:ring-2 focus:ring-[#1602F2] focus:border-[#1602F2] transition";

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) return <div className="flex justify-center items-center h-64">Loading...</div>;
  if (error) return <div className="flex justify-center items-center h-64 text-red-500">{error}</div>;
  if (!profile) return null;

  const renderProjectCard = (project: typeof topProjects[0]) => (
    <div 
      key={project.name} 
      className="bg-card rounded-lg p-4 hover:bg-accent transition-colors cursor-pointer mb-4"
    >
      <div className="flex justify-between items-start">
        <div>
          <h3 className="font-semibold text-lg flex items-center gap-2">
            {project.name}
          </h3>
          <p className="text-muted-foreground">{project.desc}</p>
        </div>
        <div className="text-right flex flex-col items-end gap-2">
          <Button size="sm" variant="default" onClick={() => {
            navigate(`/project/apply/${encodeURIComponent(project.name)}`);
          }}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAEEE8]">
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-6">
            <img src={profile.image} alt={profile.name} className="w-20 h-20 rounded-lg border-2 border-black shadow-[2px_2px_0px_#000]" />
            <div>
              {editing ? (
                <Input
                  className={inputClass + " mb-2 w-full"}
                  value={profile.name}
                  onChange={e => setProfile({ ...profile, name: e.target.value })}
                  placeholder="Name"
                />
              ) : (
                <h1 className="text-2xl font-bold text-[#FF4D1E]">{profile.name}</h1>
              )}
              {editing ? (
                <Input
                  className={inputClass + " w-full"}
                  value={profile.username}
                  onChange={e => setProfile({ ...profile, username: e.target.value })}
                  placeholder="Username"
                />
              ) : (
                <p className="text-black/30">@{profile.username}</p>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[#FF5629] font-semibold">200 Points</div>
            <div className="text-[#64748B]">Level 2</div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Editable Info Section */}
          <div className="bg-white rounded-2xl border-2 border-black shadow-[2px_2px_0px_#000] p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Profile Info</h2>
              <Button variant="outline" size="sm" onClick={() => setEditing(!editing)} className="border-2 border-black shadow-[2px_2px_0px_#000]">
                {editing ? <Save className="w-4 h-4 mr-2" /> : <Edit2 className="w-4 h-4 mr-2" />}
                {editing ? "Save" : "Edit"}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* About */}
              <div className="col-span-2">
                <label className="font-bold uppercase text-xs text-[#888] tracking-widest mb-1">About</label>
                {editing ? (
                  <textarea
                    className={inputClass + " min-h-[60px] resize-y w-full"}
                    value={profile.about}
                    onChange={e => setProfile({ ...profile, about: e.target.value })}
                  />
                ) : (
                  <p className="text-black/80 bg-[#FAF94F] rounded-lg px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000]">{profile.about}</p>
                )}
              </div>
              {/* Location */}
              <div>
                <label className="font-bold uppercase text-xs text-[#888] tracking-widest mb-1">Location</label>
                {editing ? (
                  <Input
                    className={inputClass}
                    value={profile.location}
                    onChange={e => setProfile({ ...profile, location: e.target.value })}
                  />
                ) : (
                  <p className="text-black/80 bg-[#FAF94F] rounded-lg px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000]">{profile.location}</p>
                )}
              </div>
              {/* Email */}
              <div>
                <label className="font-bold uppercase text-xs text-[#888] tracking-widest mb-1">Email</label>
                {editing ? (
                  <Input
                    className={inputClass}
                    value={profile.email}
                    onChange={e => setProfile({ ...profile, email: e.target.value })}
                  />
                ) : (
                  <p className="text-black/80 bg-[#FAF94F] rounded-lg px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000]">{profile.email}</p>
                )}
              </div>
              {/* GitHub */}
              <div>
                <label className="font-bold uppercase text-xs text-[#888] tracking-widest mb-1">GitHub</label>
                {editing ? (
                  <Input
                    className={inputClass}
                    value={profile.github}
                    onChange={e => setProfile({ ...profile, github: e.target.value })}
                  />
                ) : (
                  <p className="text-black/80 bg-[#FAF94F] rounded-lg px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000]">{profile.github}</p>
                )}
              </div>
              {/* Wallet */}
              <div>
                <label className="font-bold uppercase text-xs text-[#888] tracking-widest mb-1">Wallet</label>
                {editing ? (
                  <Input
                    className={inputClass}
                    value={profile.wallet}
                    onChange={e => setProfile({ ...profile, wallet: e.target.value })}
                  />
                ) : (
                  <p className="text-black/80 bg-[#FAF94F] rounded-lg px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000]">{profile.wallet}</p>
                )}
              </div>
              {/* Twitter */}
              <div>
                <label className="font-bold uppercase text-xs text-[#888] tracking-widest mb-1">Twitter</label>
                {editing ? (
                  <Input
                    className={inputClass}
                    value={profile.twitter}
                    onChange={e => setProfile({ ...profile, twitter: e.target.value })}
                  />
                ) : (
                  <p className="text-black/80 bg-[#FAF94F] rounded-lg px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000]">{profile.twitter}</p>
                )}
              </div>
              {/* LinkedIn */}
              <div>
                <label className="font-bold uppercase text-xs text-[#888] tracking-widest mb-1">LinkedIn</label>
                {editing ? (
                  <Input
                    className={inputClass}
                    value={profile.linkedin}
                    onChange={e => setProfile({ ...profile, linkedin: e.target.value })}
                  />
                ) : (
                  <p className="text-black/80 bg-[#FAF94F] rounded-lg px-4 py-2 border-2 border-black shadow-[2px_2px_0px_#000]">{profile.linkedin}</p>
                )}
              </div>
            </div>
          </div>

          {/* Skills */}
          <div className="bg-white rounded-2xl border-2 border-black shadow-[2px_2px_0px_#000] p-6">
            <h2 className="text-lg font-semibold mb-4">Skills</h2>
            <div className="flex flex-wrap gap-2 mb-2">
              {profile.skills.map((skill, index) => (
                <Badge key={index} variant="secondary" className="px-3 py-1 border-2 border-black shadow-[2px_2px_0px_#000] bg-[#FAF94F] text-black font-semibold">
                  {skill}
                  {editing && (
                    <button
                      type="button"
                      className="ml-2 text-red-500 hover:text-red-700 font-bold"
                      onClick={() => setProfile({ ...profile, skills: profile.skills.filter((_, i) => i !== index) })}
                    >
                      ×
                    </button>
                  )}
                </Badge>
              ))}
            </div>
            {editing && (
              <form
                onSubmit={e => {
                  e.preventDefault();
                  if (newSkill.trim()) {
                    setProfile({ ...profile, skills: [...profile.skills, newSkill.trim()] });
                    setNewSkill("");
                  }
                }}
                className="flex gap-2 mt-2"
              >
                <Input
                  className={inputClass}
                  value={newSkill}
                  onChange={e => setNewSkill(e.target.value)}
                  placeholder="Add skill"
                />
                <Button type="submit" className="border-2 border-black bg-[#FAF94F] text-black font-extrabold rounded-xl shadow-[2px_2px_0px_#000]">Add</Button>
              </form>
            )}
          </div>

          {/* Work Experience */}
          <div className="bg-white rounded-2xl border-2 border-black shadow-[2px_2px_0px_#000] p-6">
            <h2 className="text-lg font-semibold mb-4">Work Experience</h2>
            <div className="space-y-4">
              {profile.workExperience.map((work, index) => (
                <div key={index} className="flex items-start gap-4">
                  {work.logo ? (
                    <img src={work.logo} alt={work.company} className="w-12 h-12 rounded border-2 border-black shadow-[2px_2px_0px_#000]" />
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center border-2 border-black shadow-[2px_2px_0px_#000]">
                      <Briefcase className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    {editing ? (
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <Input
                          className={inputClass}
                          value={work.role}
                          onChange={e => {
                            const updated = [...profile.workExperience];
                            updated[index].role = e.target.value;
                            setProfile({ ...profile, workExperience: updated });
                          }}
                          placeholder="Role"
                        />
                        <Input
                          className={inputClass}
                          value={work.company}
                          onChange={e => {
                            const updated = [...profile.workExperience];
                            updated[index].company = e.target.value;
                            setProfile({ ...profile, workExperience: updated });
                          }}
                          placeholder="Company"
                        />
                        <Input
                          className={inputClass}
                          value={work.period}
                          onChange={e => {
                            const updated = [...profile.workExperience];
                            updated[index].period = e.target.value;
                            setProfile({ ...profile, workExperience: updated });
                          }}
                          placeholder="Period"
                        />
                        <Input
                          className={inputClass}
                          value={work.duration}
                          onChange={e => {
                            const updated = [...profile.workExperience];
                            updated[index].duration = e.target.value;
                            setProfile({ ...profile, workExperience: updated });
                          }}
                          placeholder="Duration"
                        />
                      </div>
                    ) : (
                      <>
                        <h3 className="font-semibold">{work.role}</h3>
                        <p className="text-black/60">{work.company}</p>
                        <p className="text-sm text-black/40">{work.period} • {work.duration}</p>
                      </>
                    )}
                  </div>
                  {editing && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 border-2 border-black shadow-[2px_2px_0px_#000] ml-2"
                      onClick={() => {
                        setProfile({ ...profile, workExperience: profile.workExperience.filter((_, i) => i !== index) });
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              {editing && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 border-2 border-black shadow-[2px_2px_0px_#000]"
                  onClick={() => {
                    setProfile({
                      ...profile,
                      workExperience: [
                        ...profile.workExperience,
                        { company: "", role: "", duration: "", period: "", logo: "" },
                      ],
                    });
                  }}
                >
                  Add Experience
                </Button>
              )}
            </div>
          </div>

          {/* Social Links */}
          <div className="bg-white rounded-2xl border-2 border-black shadow-[2px_2px_0px_#000] p-6">
            <h2 className="text-lg font-semibold mb-4">Social Links</h2>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(socialIcons).map(([platform, Icon]) => (
                <div key={platform} className="flex items-center gap-2">
                  <Icon className="w-5 h-5 text-black/60" />
                  {editing ? (
                    <Input
                      className={inputClass}
                      value={profile[platform as keyof Profile] as string}
                      onChange={e => setProfile({ ...profile, [platform]: e.target.value })}
                      placeholder={`Enter ${platform} username`}
                    />
                  ) : (
                    <a
                      href={`https://${platform}.com/${profile[platform as keyof Profile]}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-black/60 hover:text-black"
                    >
                      <span className="capitalize">{platform}</span>
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* GitHub Activity */}
          <div className="bg-white rounded-2xl border-2 border-black shadow-[2px_2px_0px_#000] p-6">
            <h2 className="text-lg font-semibold mb-4">GitHub Activity</h2>
            <div className="mb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-[#FAF94F] rounded-lg p-4 border-2 border-black shadow-[2px_2px_0px_#000]">
                  <div className="text-sm text-black/60">Contributions</div>
                  <div className="text-2xl font-bold">{githubStats.contributions}</div>
                </div>
                <div className="bg-[#FAF94F] rounded-lg p-4 border-2 border-black shadow-[2px_2px_0px_#000]">
                  <div className="text-sm text-black/60">Stars</div>
                  <div className="text-2xl font-bold">{githubStats.stars}</div>
                </div>
                <div className="bg-[#FAF94F] rounded-lg p-4 border-2 border-black shadow-[2px_2px_0px_#000]">
                  <div className="text-sm text-black/60">Repositories</div>
                  <div className="text-2xl font-bold">{githubStats.repos}</div>
                </div>
                <div className="bg-[#FAF94F] rounded-lg p-4 border-2 border-black shadow-[2px_2px_0px_#000]">
                  <div className="text-sm text-black/60">Followers</div>
                  <div className="text-2xl font-bold">{githubStats.followers}</div>
                </div>
              </div>
            </div>
            <div className="border-2 border-black rounded-lg p-4 bg-[#FAF94F] shadow-[2px_2px_0px_#000]">
              <div className="max-w-full mx-auto [&_.react-github-calendar__calendar]:!bg-transparent [&_.react-github-calendar__calendar]:!text-black [&_.react-github-calendar__calendar]:!fill-[#216e39]">
                <GitHubCalendar
                  username={profile.github}
                  colorScheme="light"
                  fontSize={12}
                  blockSize={12}
                  blockMargin={4}
                  hideColorLegend={false}
                  transformData={data => data.slice(-365)}
                />
              </div>
            </div>
            <div className="mt-4">
              <h3 className="text-lg font-semibold mb-2">Top Repositories</h3>
              <div className="space-y-2">
                {githubStats.projects.map((project, index) => (
                  <div key={index} className="bg-[#FAF94F] rounded-lg p-4 border-2 border-black shadow-[2px_2px_0px_#000]">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold">{project.name}</h4>
                        <p className="text-sm text-black/60">{project.lang}</p>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <span>⭐ {project.stars}</span>
                        <span>🍴 {project.forks}</span>
                        <span>🐛 {project.issues}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// To use this, run: npm install react-github-calendar 